// Google Maps Platform Weather API client.
// Uses the same REACT_APP_GOOGLE_MAPS_API_KEY as Maps; the "Weather API" must be
// enabled for that key in the Google Cloud Console.
//
// Called directly from the browser — Weather API supports CORS for any origin
// allow-listed on the API key (unlike Geocoding/Directions, which require a proxy).

import { cachedFetch } from './cacheUtils.js';

const FORECAST_TTL_MS = 60 * 60 * 1000;
const HOURLY_TTL_MS = 60 * 60 * 1000;

export const fetchForecast = async (lat, lng) => {
  if (typeof lat !== 'number' || typeof lng !== 'number') return null;
  // 2-decimal bucket (~1km) coalesces nearby jobs onto one cached forecast.
  const key = `forecast:${lat.toFixed(2)}:${lng.toFixed(2)}`;
  return cachedFetch(key, FORECAST_TTL_MS, async () => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      console.warn('Weather: REACT_APP_GOOGLE_MAPS_API_KEY missing');
      return null;
    }
    const params = new URLSearchParams({
      key: apiKey,
      'location.latitude': lat.toString(),
      'location.longitude': lng.toString(),
      days: '10',
      unitsSystem: 'METRIC',
    });
    try {
      const res = await fetch(`https://weather.googleapis.com/v1/forecast/days:lookup?${params}`);
      if (!res.ok) {
        console.warn('Weather API HTTP', res.status);
        return null;
      }
      const data = await res.json();
      const days = data?.forecastDays;
      return Array.isArray(days) && days.length > 0 ? days : null;
    } catch (err) {
      console.warn('Weather fetch failed:', err.message);
      return null;
    }
  });
};

export const findForecastForDate = (forecastDays, dateISO) => {
  if (!Array.isArray(forecastDays) || !dateISO) return null;
  const [year, month, day] = dateISO.split('-').map(Number);
  return forecastDays.find((d) =>
    d.displayDate?.year === year &&
    d.displayDate?.month === month &&
    d.displayDate?.day === day
  ) || null;
};

// Hourly forecast — Google Weather supports up to 240 hours (10 days).
// Called lazily when the user expands a day strip.
export const fetchHourlyForecast = async (lat, lng) => {
  if (typeof lat !== 'number' || typeof lng !== 'number') return null;
  const key = `hourly:${lat.toFixed(2)}:${lng.toFixed(2)}`;
  return cachedFetch(key, HOURLY_TTL_MS, async () => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      console.warn('Weather: REACT_APP_GOOGLE_MAPS_API_KEY missing');
      return null;
    }
    const params = new URLSearchParams({
      key: apiKey,
      'location.latitude': lat.toString(),
      'location.longitude': lng.toString(),
      hours: '240',
      unitsSystem: 'METRIC',
      pageSize: '240',
    });
    try {
      const res = await fetch(`https://weather.googleapis.com/v1/forecast/hours:lookup?${params}`);
      if (!res.ok) {
        console.warn('Weather hourly API HTTP', res.status);
        return null;
      }
      const data = await res.json();
      const hours = data?.forecastHours;
      return Array.isArray(hours) && hours.length > 0 ? hours : null;
    } catch (err) {
      console.warn('Weather hourly fetch failed:', err.message);
      return null;
    }
  });
};

// Filter hourly forecast to a single calendar day's work-window hours (default 6am-8pm local).
export const findHoursForDate = (hourlyForecast, dateISO, startHour = 6, endHour = 20) => {
  if (!Array.isArray(hourlyForecast) || !dateISO) return [];
  const [year, month, day] = dateISO.split('-').map(Number);
  return hourlyForecast.filter((h) => {
    const dt = h.displayDateTime || h.interval?.startTime;
    // Google Weather returns displayDateTime as a structured object: { year, month, day, hours, minutes }
    if (!dt || typeof dt !== 'object') return false;
    if (dt.year !== year || dt.month !== month || dt.day !== day) return false;
    const hr = dt.hours ?? -1;
    return hr >= startHour && hr < endHour;
  });
};

export const daysUntil = (dateISO) => {
  if (!dateISO) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateISO + 'T00:00:00');
  return Math.round((target.getTime() - today.getTime()) / 86400000);
};
